import { useReducer, useCallback, useEffect, useRef } from "react";
import useQueryParam from "./use-query-param";
import { QueryType, QueryValue } from "./query-param";

function queryParamReducer<T extends QueryType>(state: QueryParamState<T>, action: QueryParamAction<T>): QueryParamState<T> {
  switch (action.type) {
    case "SET_PARAM":
      return {
        ...state,
        [action.key]: action.value,
      };
    default:
      return state;
  }
}

export function useQueryParamReducer<T extends QueryType>(
  type: T,
  key: string,
): [QueryValue<T>, (val: QueryValue<T>, options?: any) => void] {
  const [value, originalSetValue] = useQueryParam(type, key);

  const [paramState, dispatch] = useReducer(queryParamReducer<T>, {});

  const latestSetValue = useRef<typeof originalSetValue>(originalSetValue);
  useEffect(() => {
    latestSetValue.current = originalSetValue;
  }, [originalSetValue]);

  const setValue = useCallback(
    (val: QueryValue<T>, options?: any) => {
      dispatch({ type: "SET_PARAM", key, value: val, options });
    },
    [key],
  );

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(paramState, key)) {
      latestSetValue.current(paramState[key]);
    }
  }, [paramState[key]]);

  return [value, setValue];
}

export default useQueryParamReducer;

// type QueryParamValue = string | number | boolean | null;

interface QueryParamAction<T extends QueryType> {
  type: "SET_PARAM";
  key: string;
  value: QueryValue<T>;
  options?: any;
}

type QueryParamState<T extends QueryType> = Record<string, QueryValue<T>>;